'use strict';

import LocalesConfig from '../../config/languages.json';

export default angular
  .module('maestro.services.data.users', [
    'maestro.services.utils',
    'maestro.services.webcore'
  ])
  .service('UsersService', UsersService)
  .name;

function UsersService(webcore,
                      $q,
                      $filter,
                      utils,
                      $rootScope,
                      CacheService) {

  const LOCAL_STORAGE_API_KEY = 'Auth-Key';
  const CURRENT_ADMIN_LOCAL_STORAGE_KEY = 'App-Admin';
  const $t = $filter('translate');

  /**
   * Allows to get users by supplying params object. We are not directly sending it as we better filter out params and use only those that are valid. This method uses Administrative API method.
   * @returns {Promise}
   */
  this.getUsersByParams = (organizationId, params) => {
    return this.getUsers(
      organizationId,
      params.locationId,
      params.userId,
      params.externalUserId,
      params.searchBy,
      params.searchAddress,
      params.servicePlanId,
      params.searchTag,
      params.limit,
      params.sortCollection,
      params.sortBy,
      params.sortOrder,
      params.getTags);
  };

  /**
   * Get users via Administrative API.
   * @returns {Promise}
   */
  this.getUsers = (organizationId, locationId, userId, externalUserId, searchBy, searchAddress, servicePlanId, searchTag, limit, sortCollection = 'users', sortBy = 'creationDate', sortOrder = 'desc', getTags = true) => {
    // First params that we always have values for
    const params = {
      sortCollection: sortCollection,
      sortBy: sortBy,
      sortOrder: sortOrder,
      getTags: getTags
    };

    // Optional params
    if (organizationId) {
      params.organizationId = organizationId;
    }
    if (locationId) {
      params.locationId = locationId;
    }
    if (userId) {
      params.userId = userId;
    }
    if (externalUserId) {
      params.externalUserId = externalUserId;
    }
    if (searchBy) {
      params.searchBy = searchBy;
    }
    if (limit) {
      params.limit = limit;
    }
    if (searchAddress) {
      params.searchAddress = searchAddress;
    }
    if (servicePlanId) {
      params.servicePlanId = servicePlanId;
    }
    if (searchTag) {
      params.searchTag = searchTag;
    }

    return $q.when(
      webcore.services.user.getUsers(params)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Gets currently authenticated user info.
   * @param {boolean} [forceUpdate] force update current user from server. False by default
   * @returns {Promise}
   */
  this.getCurrentUserInfo = (forceUpdate) => {
    return $q.when(
      // Cache is done inside of Webcore
      webcore.services.user.getCurrentUserInfo(forceUpdate)
        .then(data => {
          // Update storage with data
          if (forceUpdate && data.resultCode === 0) {
            utils.setStorageItem(CURRENT_ADMIN_LOCAL_STORAGE_KEY, data);
          }
          return $q.resolve(data);
        }, (err) => $q.reject(err))
    );
  };

  /**
   * Gets user information.
   * @param userId
   * @param organizationId
   * @returns {Promise}
   */
  this.getUserInfo = (userId, organizationId) => {
    return $q.when(
      webcore.services.user.getUserInfo(userId || void 0, organizationId || void 0)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Updates user with new values.
   * @param user
   * @param userId
   * @param organizationId
   * @returns {Promise}
   */
  this.updateUserInfo = (user, userId, organizationId) => {
    return $q.when(
      webcore.services.user.updateUserInfo(user, userId, { organizationId: organizationId || void 0})
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Creates new user using supplied data.
   * @param userAndLocation
   * @param strongPassword // Test password strength
   * @param organizationId // Organization to get brand name
   * @returns {Promise}
   */
  this.createNewUser = (userAndLocation, strongPassword, organizationId) => {
    let params = {
      organizationId: organizationId || void 0,
    };

    return $q.when(
      webcore.services.user.createNewUser(userAndLocation, strongPassword, params)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * This API can copy (merge) deleted user account resources to other user account.
   *
   * @param {number} userToDeleteId User ID to delete and merge.
   * @param {number} destinationUserId Merge destination user ID.
   * @returns {Promise}
   */
  this.mergeAndDeleteUser = (userToDeleteId, destinationUserId) => {
    return $q.when(
      webcore.services.user.mergeAndDeleteUser(userToDeleteId, destinationUserId)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Delete specified user account.
   * Any legal agreements the user signed will be retained in the backend.
   *
   * @param {number} userId User ID to delete.
   * @returns {Promise}
   */
  this.deleteUser = (userId) => {
    return $q.when(
      webcore.services.user.deleteUser(userId)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Saves new location data to server.
   * @param userId
   * @param locationId
   * @param location
   * @returns {Promise}
   */
  this.updateUserLocation = (userId, locationId, location) => {
    return $q.when(
      webcore.services.location.updateLocation(locationId, location)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Gets TEMP KEY that is valid for only a brief amount of time.
   * @returns {Promise}
   */
  this.getTempKey = () => {
    const brand = $rootScope.currentBrand?.brand || void 0;
    return $q.when(
      webcore.services.auth.getTempToken(brand)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Requests password reset for the specified user. This will send email with special link to user's email.
   * @param userName
   * @param brand
   * @returns {Promise}
   */
  this.resetPassword = (userName, brand) => {
    const brandName = brand || $rootScope.currentBrand?.brand || void 0;
    const appName = utils.getAppName();
    return $q.when(
      webcore.services.user.recoverPassword(userName, brandName, appName)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Gets value of the specified system property.
   * @param {string} propertyName
   * @returns {Promise<string>}
   */
  this.getSystemPropertyValue = CacheService.cachePromiseFunc((propertyName) => {
    return $q.when(
      webcore.services.user.getSystemPropertyValue(propertyName)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  });

  /**
   * This API will first attempt to return a user-specific property value in plain text.
   * If there is no user-specific property set, it will attempt to return a system-wide property value as plain text,
   *  otherwise it will return no content.
   *
   * @param {string} propertyName user or system property name
   * @param {number} [userId] User ID, used by an account with administrative privileges to retrieve the properties of another user.
   * @returns {Promise<string>}
   */
  this.getUserOrSystemPropertyValue = CacheService.cachePromiseFunc((propertyName, userId) => {
    return $q.when(
      webcore.services.user.getUserOrSystemPropertyValue(propertyName, userId)
        .then(data => $q.resolve(data?.value), err => $q.reject(err))
    );
  });

  /**
   * Gets specified user properties list.
   * @param {string|string[]} [propertyName]
   * @param {number} [userId]
   * @returns {Promise}
   */
  this.getUserProperties = (propertyName, userId) => {
    return $q.when(
      webcore.services.user.getUserProperties(propertyName, userId)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Saves user properties with values to the server.
   * @param {Array<{ name: string; content: any; }>} properties Properties to update
   * @param {number} [userId] User ID, used by an account with administrative privileges to update the properties for another user
   * @returns {Promise}
   */
  this.saveUserProperties = (properties, userId) => {
    return $q.when(
      webcore.services.user.updateUserProperties(properties, userId)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Retrieve all of the Terms of Service agreement signature identifiers to which the user has previously agreed.
   * @param {string} userId UserId to get signatures of particular user
   * @returns {Promise}
   */
  this.getSignatures = (userId) => {
    return $q.when(
      webcore.services.user.getSignatures(userId)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Hidden API to search users across cloud.
   * @param {string} [email] Email to search user by
   * @param {number} [phone] Phone to search user by
   * @returns {Promise}
   */
  this.searchUsers = (email, phone) => {
    return $q.when(
      webcore.services.user.searchUsers(email, phone)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * User account registration and retrieving admin API_KEY.
   * @param {Object} user User and location model
   * @param {string} token Valid operation token
   * @returns {Promise}
   */
  this.registerUser = (user, token) => {
    const strongPassword = true; // Test password strength
    const brand = $rootScope.currentBrand?.brand || void 0;
    return $q.when(
      webcore.services.user.registerNewUser(user, token, strongPassword, brand)
        .then(data => {
          if (data && data.key) {
            utils.setStorageItem(LOCAL_STORAGE_API_KEY, data.key);
            return webcore.services.auth.loginByKey(data.key, true, brand)
              .then(() => $q.resolve(data));
          } else {
            return $q.reject(data);
          }
        }, err => $q.reject(err))
    );
  };

  /**
   * Returns array of the languages supported by system
   * @returns {{id: string, name: string}[]}
   */
  this.getSupportedLanguages = () => {
    const result = [
      {
        id: 'en',
        name: $t('lang.en')
      }
    ];

    return $q.when(
      webcore.services.systemProperties.getCloudLanguages()
        .then((langs) => {
          langs = langs || [];
          langs.forEach(lng => {
            if (lng !== 'en') {
              let language = LocalesConfig.list.find(el => el.code === lng);
              result.push({
                id: lng,
                name: language.name
              });
            }
          });

          return $q.resolve(result);
        }, () => $q.resolve(result))
    );
  };

  /**
   * Get current user preferred System of measurement
   * @return {Promise<'metric' | 'us'>}
   */
  this.getSystemOfMeasurement = CacheService.cachePromiseFunc(() => {
    return $q.when(webcore.services.systemProperties.getSystemOfMeasurement());
  });

  /**
   * Save current user preferred System of measurement
   * @param {'metric' | 'us'} systemOfMeasurement
   */
  this.updateSystemOfMeasurement = (systemOfMeasurement) => {
    systemOfMeasurement = (systemOfMeasurement === 'metric' || systemOfMeasurement === 'us')
      ? systemOfMeasurement
      : 'us';
    return $q.when(webcore.services.systemProperties.updateSystemOfMeasurement(systemOfMeasurement))
      .then(result => {
        this.getSystemOfMeasurement.clearEntireCache();
        return result;
      });
  };

}
